import { useEffect, useMemo, useState } from 'react';
import Icon from './Icon';
import { fetchShareholderOwnership } from '../shareholderOwnershipApi';
import { useLanguage } from '../i18n/language';
import './BranchesPage.css';
import './ShareholderOwnershipPage.css';

export default function ShareholderOwnershipPage() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const numLocale = isAr ? 'ar-SA' : 'en-US';

  const t = useMemo(() => ({
    title: isAr ? 'حصتي في الشركة' : 'My ownership',
    subtitle: isAr ? 'تفاصيل ملكيتك الحالية ومقارنة سريعة مع كبار المساهمين' : 'Your current stake and a quick comparison with major shareholders',
    loadFailed: isAr ? 'فشل التحميل' : 'Failed to load',
    loading: isAr ? 'جارٍ تحميل بيانات الملكية...' : 'Loading ownership data...',
    statMyPct: isAr ? 'نسبة ملكيتي' : 'My ownership %',
    statMyShares: isAr ? 'عدد أسهمي' : 'My shares',
    statRank: isAr ? 'ترتيبي بين المساهمين' : 'Rank among shareholders',
    statLargest: isAr ? 'أكبر نسبة مساهم' : 'Largest shareholder %',
    coverageLabel: isAr ? 'مساهمة حصتك مقابل رأس المال' : 'Your stake vs capital',
    totalShares: isAr ? 'إجمالي الأسهم:' : 'Total shares:',
    shareholdersCount: isAr ? 'عدد المساهمين:' : 'Shareholders:',
    colShareholder: isAr ? 'المساهم' : 'Shareholder',
    colOwnership: isAr ? 'نسبة الملكية' : 'Ownership',
    colShares: isAr ? 'عدد الأسهم' : 'Shares',
    emptyPeers: isAr ? 'لا توجد بيانات مساهمين متاحة حالياً.' : 'No shareholder data available.',
  }), [isAr]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const payload = await fetchShareholderOwnership();
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : t.loadFailed);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t.loadFailed]);

  const topPeers = useMemo(() => (data?.peers ?? []).slice(0, 10), [data]);

  return (
    <div className="page shown-page">
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title">
              <Icon name="money" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />
              {t.title}
            </span>
            <span className="list-page-card__sub">{t.subtitle}</span>
          </div>
        </header>

        {error && <div className="list-page-card__banner list-page-card__banner--error">{error}</div>}
        {loading ? (
          <div className="list-page-card__loading">{t.loading}</div>
        ) : (
        <>
          <div className="shown-stats">
            <div className="shown-stat-card">
              <div className="shown-stat-label">{t.statMyPct}</div>
              <div className="shown-stat-value">{data?.myOwnershipPct?.toLocaleString(numLocale)}%</div>
            </div>
            <div className="shown-stat-card">
              <div className="shown-stat-label">{t.statMyShares}</div>
              <div className="shown-stat-value">{data?.myShares?.toLocaleString(numLocale)}</div>
            </div>
            <div className="shown-stat-card">
              <div className="shown-stat-label">{t.statRank}</div>
              <div className="shown-stat-value">#{data?.rankAmongShareholders || '—'}</div>
            </div>
            <div className="shown-stat-card">
              <div className="shown-stat-label">{t.statLargest}</div>
              <div className="shown-stat-value">{data?.largestShareholderPct?.toLocaleString(numLocale)}%</div>
            </div>
          </div>

          <div className="shown-coverage">
            <div className="shown-coverage__label">{t.coverageLabel}</div>
            <div className="shown-coverage__bar">
              <div className="shown-coverage__fill" style={{ width: `${Math.min(100, Math.max(0, data?.myOwnershipPct ?? 0))}%` }} />
            </div>
            <div className="shown-coverage__meta">
              <span>{t.totalShares} {data?.totalShares?.toLocaleString(numLocale)}</span>
              <span>{t.shareholdersCount} {data?.shareholdersCount?.toLocaleString(numLocale)}</span>
            </div>
          </div>

          <div className="table-card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t.colShareholder}</th>
                  <th>{t.colOwnership}</th>
                  <th>{t.colShares}</th>
                </tr>
              </thead>
              <tbody>
                {topPeers.map((p, idx) => (
                  <tr key={`${p.name}-${idx}`} className={p.isMe ? 'shown-row--me' : ''}>
                    <td>{p.name}</td>
                    <td>{Number(p.ownershipPct ?? 0).toLocaleString(numLocale)}%</td>
                    <td>{Number(p.shares ?? 0).toLocaleString(numLocale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {topPeers.length === 0 && <div className="shown-empty">{t.emptyPeers}</div>}
          </div>
        </>
      )}
      </div>
    </div>
  );
}
